import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle } from "lucide-react";
import { getChatHistory, sendChat } from "@/lib/api";

export default function ChatTab({ subject }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    getChatHistory(subject.id).then(setMessages).catch(() => {});
  }, [subject.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const submit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", text };
    setMessages((m) => [...m, tempUser]);
    try {
      const res = await sendChat(subject.id, text);
      setMessages((m) => {
        const merged = [...m.filter((x) => x.id !== tempUser.id), res.user, res.assistant];
        const map = new Map(merged.map((x) => [x.id, x]));
        return Array.from(map.values());
      });
    } catch {
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", text: "Hmm, I couldn't reply. Try again." }]);
    } finally { setSending(false); }
  };

  const quickQuestions = [
    "Summarize my notes in 5 bullet points",
    "Quiz me with 5 practice questions",
    "Explain the hardest concept simply",
  ];

  return (
    <div className="nb-card p-0 overflow-hidden h-[70vh] flex flex-col">
      <div className="p-4 nb-border border-l-0 border-r-0 border-t-0 bg-[#D4C4FB] dark:bg-[#B794F4] text-black flex items-center gap-2">
        <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
        <span className="font-display font-extrabold text-lg">AI Study Tutor, {subject.name}</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-scroll">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="font-display text-xl font-extrabold mb-3">Ask me anything about {subject.name}</div>
            <div className="flex flex-wrap gap-2 justify-center">
              {quickQuestions.map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  data-testid={`chat-quick-${q.slice(0,10)}`}
                  className="nb-border rounded-full px-3 py-1.5 bg-white dark:bg-zinc-900 text-xs font-bold hover:nb-shadow"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={`chat-msg-${m.role}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`nb-border rounded-2xl px-4 py-3 max-w-[80%] whitespace-pre-wrap font-medium ${
                m.role === "user"
                  ? "bg-white dark:bg-zinc-900"
                  : "bg-[#9DFFB0] dark:bg-[#68D391] text-black"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="nb-border rounded-2xl px-4 py-3 bg-[#9DFFB0] dark:bg-[#68D391] text-black font-bold animate-pulse">
              Thinking…
            </div>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="p-3 nb-border border-l-0 border-r-0 border-b-0 flex gap-2 bg-white dark:bg-zinc-900">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${subject.name}…`}
          data-testid="chat-input"
          className="nb-input flex-1"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={sending || !input.trim()}
          className="nb-button px-4 py-2 bg-[#D4C4FB] dark:bg-[#B794F4]"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
}
